import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cachedGet, download, errMsg } from "@/lib/api";
import { Loading, PctBar, Stat, useStageNames, Variance, RagBadge, fmtDate } from "@/components/app/bits";

function Dossier({ system }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await download(`/systems/${system.id}/dossier`, `Dossier_${system.code}.pdf`); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <button onClick={go} disabled={busy} data-testid={`dossier-btn-${system.code}`} className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}PDF
    </button>
  );
}

const StageCells = ({ p }) => ["A", "B", "C"].map((s) => <td key={s} className="px-3 py-3 w-36"><PctBar pct={p[s].pct} /></td>);

export default function OverviewTab({ project }) {
  const sn = useStageNames();
  const [data, setData] = useState(null);
  const [ms, setMs] = useState([]);
  const [open, setOpen] = useState({});
  useEffect(() => {
    cachedGet(`/projects/${project.id}/progress`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    cachedGet(`/projects/${project.id}/milestones`).then((r) => setMs(r.data)).catch(() => {});
  }, [project.id]);
  if (!data) return <Loading />;
  const p = data.project;
  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Overall complete" value={`${p.overall.pct}%`} sub={`${p.overall.done.toLocaleString("en-GB")} of ${p.overall.total.toLocaleString("en-GB")} checksheets`} accent="text-amber-400" testid="stat-overall" />
        {["A", "B", "C"].map((s) => <Stat key={s} label={`${s} · ${sn[s]}`} value={p[s].pct === null ? "-" : `${p[s].pct}%`} sub={`${p[s].done} / ${p[s].total}`} testid={`stat-stage-${s}`} />)}
        <Stat label="Open cat A punch" value={project.open_cat_a} accent="text-red-400" testid="stat-cat-a" />
      </div>
      {ms.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="overview-milestones">
          {ms.map((m) => (
            <div key={m.id} className={`panel p-4 ${m.is_key ? "border-amber-500/50" : ""}`}>
              <div className="flex justify-between items-start gap-2"><p className="font-heading text-lg font-semibold text-slate-100">{m.name}</p><RagBadge rag={m.forecast.rag} /></div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><p className="text-slate-500 font-mono uppercase text-[10px]">Target</p><p className="num text-slate-200">{fmtDate(m.target_date)}</p></div>
                <div><p className="text-slate-500 font-mono uppercase text-[10px]">Forecast</p><p className="num text-amber-400">{m.forecast.forecast_date ? fmtDate(m.forecast.forecast_date) : "Stalled"}</p></div>
                <div><p className="text-slate-500 font-mono uppercase text-[10px]">Variance</p><Variance days={m.forecast.variance_days} /></div>
              </div>
            </div>
          ))}
        </div>
      )}
      <section>
        <h2 className="text-xl font-semibold text-slate-100 mb-3">Progress by system</h2>
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="systems-progress-table">
            <thead><tr className="border-b border-[#1E2D4A] text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">System / subsystem</th>{["A", "B", "C"].map((s) => <th key={s} className="px-3 py-3">{s} · {sn[s]}</th>)}<th className="px-3 py-3">Overall</th><th className="px-3 py-3">Dossier</th>
            </tr></thead>
            <tbody>
              {data.systems.map((sy) => (
                <Fragment key={sy.id}>
                  <tr className="border-b border-[#1E2D4A] hover:bg-[#131C31] cursor-pointer" onClick={() => setOpen({ ...open, [sy.id]: !open[sy.id] })} data-testid={`system-row-${sy.code}`}>
                    <td className="px-4 py-3"><span className="flex items-center gap-2">{open[sy.id] ? <ChevronDown className="w-4 h-4 text-amber-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}<span className="num text-slate-400">{sy.code}</span><span className="text-slate-100 font-medium">{sy.name}</span></span></td>
                    <StageCells p={sy.progress} /><td className="px-3 w-36"><PctBar pct={sy.progress.overall.pct} /></td><td className="px-3"><Dossier system={sy} /></td>
                  </tr>
                  {open[sy.id] && sy.subsystems.map((sb) => (
                    <tr key={sb.id} className="border-b border-[#1E2D4A] bg-[#0B1222]" data-testid={`subsystem-row-${sb.code}`}>
                      <td className="pl-12 pr-4 py-2.5"><span className="num text-slate-500 mr-2">{sb.code}</span><span className="text-slate-300">{sb.name}</span></td>
                      <StageCells p={sb.progress} /><td className="px-3"><PctBar pct={sb.progress.overall.pct} /></td><td />
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section>
        <h2 className="text-xl font-semibold text-slate-100 mb-3">Progress by discipline</h2>
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="discipline-progress-table">
            <thead><tr className="border-b border-[#1E2D4A] text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Discipline</th>{["A", "B", "C"].map((s) => <th key={s} className="px-3 py-3">{s}</th>)}<th className="px-3 py-3">Overall</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.disciplines).map(([d, pr]) => (
                <tr key={d} className="border-b border-[#1E2D4A] last:border-0"><td className="px-4 py-3 capitalize text-slate-200">{d}</td><StageCells p={pr} /><td className="px-3 w-36"><PctBar pct={pr.overall.pct} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
